import React, { useState } from 'react';
import { BORD_CREDIT_PAYMENT_TYPES, IOrderFinances, ORDER_STATUS_ID, PAYMENT_TYPES } from 'types/orders';
import { ICountryDetail } from 'types/countries';
import { formatShortDate } from 'utils/formatDatesAndHours';
import { updateOrderStatus } from 'services/orders';
import { displayErrorNotification, displaySuccessNotification } from 'utils/displayNudosStandardNotifications';
import { useHistory } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import useOrdersFinancesModule from 'views/TrackingOrder/components/OrdersListingFinances/useOrdersFinancesModule';
import { IconNoteOnPaper } from 'assets/DesignSystem/SVGComponents';
import NudosConfirmActionModal from 'components/NudosComponents/NudosConfirmActionModal/NudosConfirmActionModal';
import { getOrganizationCredit } from 'services/bussines';
import { BordOneToneIcon } from 'components/NudosComponents';
import './HeaderCard.scss';

const HeaderCard = ({ data, openModal, isCancelled, refreshData }: IHeaderCard) => {
  const { createdAt, order, destinationCountry, orderId } = data;
  const [loadingActivate, setLoadingActivate] = useState<boolean>(false);
  const [showActivateConfirmModal, setShowActivateConfirmModal] = useState<boolean>(false);
  const history = useHistory();
  const { mutate } = useSWRConfig();
  const destinationCountryId = destinationCountry?.id;
  const destinationCountryName = destinationCountry?.name;
  const destinationCountryCode = destinationCountry?.code;
  const destinationCountryFlag = destinationCountry?.flag;

  const hasSomeProductByQuote = order.sales?.some(sale => sale?.quoteItemId);

  const activateOrderModalTitle = '¿Estás seguro que quieres activar la orden?';
  const activateOrderModalText =
    'Esta acción reactivará todos los envíos de la orden. Deberá realizar el pago y cargar el comprobante nuevamente.';
  const activateOrderModalBackButton = 'Volver atrás';
  const activateOrderModalConfirmButton = 'Sí, continuar';

  const { organizationName, organizationId, orderCancelledAt, orderStatusLog } = order;
  const cancelledDateStatusLog = orderStatusLog?.[0]?.createdAt;
  const cancelledDate = cancelledDateStatusLog || orderCancelledAt;
  const { orderFilters, setOrderFilters } = useOrdersFinancesModule();
  const formattedCreationDateShort = createdAt ? formatShortDate(createdAt)?.onlyDate : 'Sin fecha';
  const formattedCancelingDate = cancelledDate ? formatShortDate(cancelledDate)?.onlyDate : 'Sin fecha';
  const showActiveOrder = isCancelled && !!cancelledDate;
  const ORDER_QUERY_KEYS_TO_INVALIDATE = [
    'orders-finances-pending',
    'orders-finances-confirmed',
    'orders-finances-delivered',
    'orders-finances-cancelled',
    'orders-cancelled-modal',
    'orders-operations'
  ];
  const updateOrCreateFilter = (filterName: string, filterValue: string) => {
    const existingFilterIndex = orderFilters.findIndex(filter => filter?.name === filterName);
    const existingFilter = existingFilterIndex >= 0 ? orderFilters[existingFilterIndex] : null;

    if (existingFilter?.values.includes(filterValue)) {
      const remainingValues = existingFilter?.values?.filter(value => value !== filterValue);

      const updatedFilters =
        remainingValues?.length > 0
          ? orderFilters.map((filter, index) =>
              index === existingFilterIndex ? { ...filter, values: remainingValues } : filter
            )
          : orderFilters.filter((_, index) => index !== existingFilterIndex);

      setOrderFilters(updatedFilters);
      return;
    }

    const updatedFilters = existingFilter
      ? orderFilters.map((filter, index) =>
          index === existingFilterIndex ? { ...filter, values: [filterValue] } : filter
        )
      : [...orderFilters, { name: filterName, includes: 'in', values: [filterValue] }];

    setOrderFilters(updatedFilters);
  };

  const handleFilterByCountry = (e: React.MouseEvent, country: ICountryDetail) => {
    e.stopPropagation();
    const countryName = country?.name;
    if (!countryName) return;

    updateOrCreateFilter('countryDestination', countryName);
  };

  const handleFilterByOrganization = (e: React.MouseEvent, organizationId?: number) => {
    e.stopPropagation();
    if (!organizationId) return;

    updateOrCreateFilter('company', String(organizationId));
  };

  const getShipmentFlags = (): JSX.Element | null => {
    if (!destinationCountry) return null;
    const shipmentCountry: ICountryDetail = {
      id: destinationCountryId,
      name: destinationCountryName,
      code: destinationCountryCode,
      flag: destinationCountryFlag
    };
    return (
      <div
        key={`shipmentFlag-0-shipmentCountry-${shipmentCountry?.id}`}
        className="shipmentDestinationFlags"
        onClick={e => e.stopPropagation()}
      >
        <div
          onClick={e => handleFilterByCountry(e, shipmentCountry)}
          className="flag"
          style={{ backgroundImage: `url("${shipmentCountry?.flag || ''}")` }}
        />
        <span className="organizationName" onClick={e => handleFilterByOrganization(e, organizationId)}>
          {organizationName}
        </span>
      </div>
    );
  };

  const handleActivateOrder = async () => {
    if (!orderId || loadingActivate) return;

    setLoadingActivate(true);
    try {
      const { order } = data;
      const { paymentLink, committedPaymentDate, invoiceRecords } = order || {};
      const invoice = invoiceRecords?.[0];
      const invoiceNumber = invoice?.id || '';
      const formData = new FormData();

      const orderPaymentType = order.PaymentType?.name as PAYMENT_TYPES;
      const isBordCreditPayment = BORD_CREDIT_PAYMENT_TYPES.includes(orderPaymentType);
      const newOrderStatus = isBordCreditPayment ? ORDER_STATUS_ID.CONFIRMED : ORDER_STATUS_ID.PENDING_FOR_CONFIRMATION;

      const getPaymentDate = async () => {
        if (!isBordCreditPayment) return committedPaymentDate;
        const { creditForOrder } = await getOrganizationCredit(organizationId);
        const paymentDate = new Date();
        paymentDate.setDate(paymentDate.getDate() + creditForOrder.approvedDays);
        return paymentDate;
      };

      const bodyForm = {
        invoiceNumber: invoiceNumber,
        paymentLink: paymentLink,
        committedPaymentDate: await getPaymentDate()
      };
      const bodyJson = JSON.stringify(bodyForm);
      data && formData.append('body', bodyJson);

      await updateOrderStatus(orderId, newOrderStatus, formData);

      displaySuccessNotification({
        customJSXMessage: <>Se reactivó con éxito la orden N°{orderId}</>
      });

      ORDER_QUERY_KEYS_TO_INVALIDATE.forEach(queryKey => {
        mutate(
          key => {
            if (Array.isArray(key)) {
              return key?.length > 0 && key[0] === queryKey;
            }
            if (typeof key === 'string') {
              return key === queryKey || key.startsWith(queryKey);
            }
            return false;
          },
          undefined,
          { revalidate: true }
        );
      });

      window.dispatchEvent(new CustomEvent('orderStatusChanged', { detail: { orderId } }));

      if (refreshData) {
        await refreshData();
      }

      handleNavigateToOrderDetails();
    } catch (error) {
      displayErrorNotification({
        customJSXMessage: <>Error al reactivar la orden. Por favor intenta de nuevo</>
      });
    } finally {
      setLoadingActivate(false);
    }
  };

  const handleNavigateToOrderDetails = () => {
    history.push(`/order/details/${orderId}`);
  };

  const handleActivateOrderClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowActivateConfirmModal(true);
  };

  const handleCloseActivateConfirmModal = () => setShowActivateConfirmModal(false);

  const handleConfirmActivateOrder = async () => {
    await handleActivateOrder();
    setShowActivateConfirmModal(false);
  };

  return (
    <div className={`headerCard ${isCancelled ? 'cancelled' : ''}`} onClick={handleNavigateToOrderDetails}>
      <div className="firstRow">
        <div className="numberOrderContainer">
          {hasSomeProductByQuote && <BordOneToneIcon variant="quotation" customWidth="1rem" className="quoteIcon" />}
          <div className="numberOrder">N°{orderId}</div>
        </div>
        <div className="orderStatusDate">
          {showActiveOrder && (
            <div className={`activeOrder ${loadingActivate ? 'loading' : ''}`} onClick={handleActivateOrderClick}>
              {loadingActivate ? 'Activando...' : 'Activar'}
            </div>
          )}
          <span className={`orderStatus ${isCancelled ? 'cancelled' : ''}`}>
            {isCancelled ? 'Cancelada:' : 'Solicitada:'}
          </span>
          <span className={`orderDate ${isCancelled ? 'cancelled' : ''}`}>
            {isCancelled ? formattedCancelingDate : formattedCreationDateShort}
          </span>
        </div>
      </div>
      <div className="secondRow">
        <div className="infoCountry">{destinationCountry && getShipmentFlags()}</div>
        <div className="actionModalComments" onClick={e => e.stopPropagation()}>
          <button className="openOrderCommentsBtn" onClick={openModal} aria-label="Abrir comentarios de la orden">
            <IconNoteOnPaper />
          </button>
        </div>
      </div>
      {showActivateConfirmModal && (
        <div onClick={e => e.stopPropagation()} role="presentation">
          <NudosConfirmActionModal
            modalTitle={activateOrderModalTitle}
            modalText={activateOrderModalText}
            firstButtonText={activateOrderModalBackButton}
            secondButtonText={activateOrderModalConfirmButton}
            firstButtonAction={handleCloseActivateConfirmModal}
            secondButtonAction={handleConfirmActivateOrder}
            isLoading={loadingActivate}
            customHeight="254px"
          />
        </div>
      )}
    </div>
  );
};

interface IHeaderCard {
  data: IOrderFinances;
  openModal: () => void;
  isCancelled?: boolean;
  queryParams?: string;
  setQueryParams?: (queryParams: string) => void;
  refreshData?: () => Promise<void>;
}
export default HeaderCard;
